import React, { useState, useRef } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Upload, Link2, ClipboardPaste, Loader2, FileSpreadsheet, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * Parse "front,back,example" or "front\tback\texample" text.
 * Auto-detects header row (skips first row if it looks like headers).
 * Returns [{front, back, example}]
 */
function parseText(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const detectSep = (line) => (line.includes("\t") ? "\t" : ",");
  const sep = detectSep(lines[0]);
  const rows = lines.map((l) => {
    // simple CSV split (no quoted-comma handling — for advanced needs, users can save as tab-separated)
    return l.split(sep).map((c) => c.trim().replace(/^"|"$/g, ""));
  });
  // Detect header
  const first = rows[0].map((c) => c.toLowerCase());
  const hasHeader = first.some((c) => ["front", "back", "example", "english", "translation", "meaning"].includes(c));
  const data = hasHeader ? rows.slice(1) : rows;
  return data
    .filter((r) => r[0] && r[1])
    .map((r) => ({ front: r[0], back: r[1], example: r[2] || "" }));
}

function parseSheet(arrayBuf) {
  const wb = XLSX.read(arrayBuf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
  if (!rows.length) return [];
  const first = (rows[0] || []).map((c) => String(c || "").toLowerCase());
  const hasHeader = first.some((c) => ["front", "back", "example", "english", "translation", "meaning"].includes(c));
  const data = hasHeader ? rows.slice(1) : rows;
  return data
    .filter((r) => r && r[0] && r[1])
    .map((r) => ({
      front: String(r[0]).trim(),
      back: String(r[1]).trim(),
      example: r[2] ? String(r[2]).trim() : "",
    }));
}

/** Convert a Google Sheets URL to its CSV export URL. */
function sheetsUrlToCsv(url) {
  // https://docs.google.com/spreadsheets/d/{SHEET_ID}/edit#gid={GID}
  const m = url.match(/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (!m) return null;
  const id = m[1];
  const gidMatch = url.match(/gid=(\d+)/);
  const gid = gidMatch ? gidMatch[1] : "0";
  return `https://docs.google.com/spreadsheets/d/${id}/export?format=csv&gid=${gid}`;
}

export default function BulkImport({ courseId, language, onImported }) {
  const [mode, setMode] = useState("paste"); // paste | file | sheets
  const [text, setText] = useState("");
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [preview, setPreview] = useState([]);
  const [limit, setLimit] = useState("");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const clear = () => {
    setPreview([]);
    setText("");
    setSheetsUrl("");
    setLimit("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePaste = () => {
    if (!text.trim()) return toast.error("Paste some rows first");
    const rows = parseText(text);
    if (!rows.length) return toast.error("Couldn't detect any rows. Format: front,back,example");
    setPreview(rows);
    toast.success(`Parsed ${rows.length} rows`);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const isCsv = /\.(csv|tsv|txt)$/i.test(file.name);
      let rows;
      if (isCsv) {
        rows = parseText(await file.text());
      } else {
        rows = parseSheet(await file.arrayBuffer());
      }
      if (!rows.length) throw new Error("No rows found");
      setPreview(rows);
      toast.success(`Parsed ${rows.length} rows from ${file.name}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't parse file");
    } finally {
      setLoading(false);
    }
  };

  const handleSheets = async () => {
    if (!sheetsUrl.trim()) return toast.error("Paste a Google Sheets URL");
    const csvUrl = sheetsUrlToCsv(sheetsUrl);
    if (!csvUrl) return toast.error("Not a valid Google Sheets URL");
    setLoading(true);
    try {
      const resp = await fetch(csvUrl);
      if (!resp.ok) throw new Error("Fetch failed — make sure the sheet is public ('Anyone with the link')");
      const csv = await resp.text();
      const rows = parseText(csv);
      if (!rows.length) throw new Error("No rows detected in the sheet");
      setPreview(rows);
      toast.success(`Parsed ${rows.length} rows from Google Sheets`);
    } catch (err) {
      toast.error(err?.message || "Fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const capped = limit ? preview.slice(0, parseInt(limit)) : preview;

  const doImport = async () => {
    if (!capped.length) return;
    // No courseId → new-course flow: just hand cards back to the parent, no API call yet.
    if (!courseId) {
      onImported?.(capped);
      toast.success(`Added ${capped.length} card${capped.length === 1 ? "" : "s"} to draft`);
      clear();
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/courses/${courseId}/cards/bulk`, {
        cards: capped,
        limit: limit ? parseInt(limit) : null,
      });
      toast.success(`Imported ${data.inserted} cards 🎉`);
      onImported?.(data.cards);
      clear();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const modes = [
    { id: "paste", label: "Paste", icon: ClipboardPaste },
    { id: "file", label: "Upload file", icon: Upload },
    { id: "sheets", label: "Google Sheets", icon: Link2 },
  ];

  return (
    <div className="brutal-card p-6 bg-brand-mint/60 mb-6" data-testid="bulk-import">
      <h2 className="font-display text-xl font-bold text-brand-navy mb-1 flex items-center gap-2">
        <FileSpreadsheet className="w-5 h-5" strokeWidth={3} /> Bulk import
      </h2>
      <p className="text-brand-navy/70 text-sm mb-4">
        Import a list of cards from a spreadsheet. Columns must be in order: <b>front (target language)</b>, <b>back (English)</b>, <i>example (optional)</i>.
      </p>

      <div className="flex gap-2 mb-4 flex-wrap">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => { setMode(m.id); setPreview([]); }}
            data-testid={`bulk-mode-${m.id}`}
            className={`brutal-badge px-4 py-2 ${mode === m.id ? "bg-brand-yellow" : "bg-white hover:bg-brand-cream"}`}
          >
            <m.icon className="w-3.5 h-3.5" strokeWidth={3} /> {m.label}
          </button>
        ))}
      </div>

      {mode === "paste" && (
        <div className="space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="bulk-paste-textarea"
            placeholder={`Bonjour\tHello\tBonjour, ça va ?\nMerci\tThank you\nAu revoir\tGoodbye`}
            className="brutal-input min-h-[140px] bg-white font-mono text-sm"
          />
          <div className="flex justify-end">
            <button type="button" onClick={handlePaste} data-testid="bulk-paste-parse" className="brutal-btn-white text-sm py-2">
              Parse
            </button>
          </div>
        </div>
      )}

      {mode === "file" && (
        <div className="space-y-3">
          <label className="brutal-card-sm bg-white p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-brand-cream" data-testid="bulk-file-drop">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls"
              onChange={handleFile}
              className="hidden"
            />
            <Upload className="w-8 h-8 text-brand-navy" strokeWidth={2.5} />
            <div className="font-bold text-brand-navy">Click to choose CSV / TSV / Excel file</div>
            <div className="text-xs text-brand-navy/60">or drop it here — max 1000 rows</div>
          </label>
        </div>
      )}

      {mode === "sheets" && (
        <div className="space-y-3">
          <input
            value={sheetsUrl}
            onChange={(e) => setSheetsUrl(e.target.value)}
            data-testid="bulk-sheets-url"
            placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0"
            className="brutal-input bg-white"
          />
          <p className="text-xs text-brand-navy/70">
            Set the sheet's share access to <b>"Anyone with the link — Viewer"</b> so we can fetch it.
          </p>
          <div className="flex justify-end">
            <button type="button" onClick={handleSheets} disabled={loading} data-testid="bulk-sheets-fetch" className="brutal-btn-white text-sm py-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" strokeWidth={3} />} Fetch
            </button>
          </div>
        </div>
      )}

      {loading && <div className="mt-4 flex items-center gap-2 text-brand-navy/70"><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</div>}

      {preview.length > 0 && (
        <div className="mt-6" data-testid="bulk-preview">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="font-display font-bold text-brand-navy">
              Preview — <span className="text-brand-pink">{capped.length}</span> of {preview.length} rows
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-brand-navy uppercase">Limit</label>
              <input
                type="number"
                min={1}
                max={preview.length}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                data-testid="bulk-limit"
                placeholder={String(preview.length)}
                className="brutal-input w-24 bg-white py-2"
              />
              <button onClick={clear} type="button" className="brutal-btn-white text-xs py-2" title="Clear preview">
                <X className="w-3.5 h-3.5" strokeWidth={3} />
              </button>
            </div>
          </div>
          <div className="brutal-card-sm bg-white overflow-hidden">
            <div className="max-h-64 overflow-y-auto divide-y-2 divide-brand-ink/10">
              {capped.slice(0, 200).map((r, i) => (
                <div key={`${r.front}-${i}`} className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 text-sm">
                  <div className="truncate"><span className="text-brand-navy/50 text-xs mr-1">{i + 1}.</span><span className="font-bold text-brand-navy">{r.front}</span></div>
                  <div className="truncate text-brand-pink font-semibold">{r.back}</div>
                  <div className="truncate text-brand-navy/60 italic">{r.example}</div>
                </div>
              ))}
              {capped.length > 200 && (
                <div className="p-2 text-xs text-brand-navy/60 text-center">
                  … {capped.length - 200} more rows (not shown, but will be imported)
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={doImport}
              disabled={importing}
              data-testid="bulk-import-btn"
              className="brutal-btn-pink text-sm py-2"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={3} />}
              {courseId ? `Import ${capped.length} card${capped.length === 1 ? "" : "s"}` : `Add ${capped.length} to draft`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
